import React from "react";
import { CloseOutlined, LoadingOutlined } from "@ant-design/icons";
import { modalStyles } from "../../../utils/modalStyles";

// Споделени примитиви за ordering модалите — стилът на „Промени" (EditModifiersModal,
// демо прототипа): pos-* Tailwind токени, радиус 20, топъл скрим. Виж скила
// cloudts-marking-redesign §2 „Модали".

/** antd Modal styles за модула „Маркиране" — подава се вместо modalStyles. */
// eslint-disable-next-line react-refresh/only-export-components
export const posModalStyles = {
  ...modalStyles,
  mask: {
    ...modalStyles.mask,
    background: "rgba(40,30,20,.45)",
  },
  content: {
    ...modalStyles.content,
    borderRadius: 20,
    boxShadow: "0 24px 60px rgba(0,0,0,.3)",
  },
};

export const CloseChip = ({ onClose }: { onClose: () => void }) => (
  <button
    type="button"
    onClick={onClose}
    aria-label="close"
    // Прозрачен фон — чипчето поема цвета на хедъра под себе си (бял или
    // кремав „panel"), както ghost бутона във футъра
    className="grid h-[38px] w-[38px] flex-shrink-0 cursor-pointer place-items-center rounded-[10px] border border-pos-line2 bg-transparent text-pos-dim transition-colors hover:text-pos-ink"
  >
    <CloseOutlined className="text-[15px]" />
  </button>
);

export const IconChip = ({
  icon,
  size = 40,
}: {
  icon: React.ReactNode;
  size?: number;
}) => (
  <div
    className="grid flex-shrink-0 place-items-center rounded-[11px] bg-pos-ready-bg text-pos-accent"
    style={{ width: size, height: size, fontSize: size >= 40 ? 20 : 16 }}
  >
    {icon}
  </div>
);

export const ModalHeader = ({
  icon,
  title,
  subtitle,
  onClose,
  isNarrow,
  noDivider,
  tone = "card",
  subtitleBelowOnNarrow,
}: {
  icon: React.ReactNode;
  title: React.ReactNode;
  subtitle?: string;
  onClose: () => void;
  isNarrow: boolean;
  noDivider?: boolean;
  /** „panel" = кремав хедър (модалът за закриване); default бял. */
  tone?: "card" | "panel";
  /** На тесен екран дълго подзаглавие слиза на собствен ред под иконата и
   *  заглавието — на цялата ширина, центрирано, на един ред. */
  subtitleBelowOnNarrow?: boolean;
}) => {
  const subtitleBelow = !!subtitle && isNarrow && !!subtitleBelowOnNarrow;
  return (
    <div
      className={`flex flex-col gap-2 ${
        tone === "panel" ? "bg-pos-panel" : "bg-pos-card"
      } ${isNarrow ? "px-4 py-3.5" : "px-[22px] py-4"} ${
        noDivider ? "" : "border-b border-pos-line2"
      }`}
    >
      <div
        className={`flex gap-3 ${
          subtitle && !subtitleBelow ? "items-start" : "items-center"
        }`}
      >
        <IconChip icon={icon} />
        <div className="min-w-0 flex-1">
          <h2 className="m-0 text-lg font-extrabold leading-tight text-pos-ink">
            {title}
          </h2>
          {subtitle && !subtitleBelow && (
            <p className="m-0 mt-0.5 text-[13px] font-medium leading-normal text-pos-muted">
              {subtitle}
            </p>
          )}
        </div>
        <CloseChip onClose={onClose} />
      </div>
      {subtitleBelow && (
        <p className="m-0 truncate text-center text-[13px] font-medium leading-normal text-pos-muted">
          {subtitle}
        </p>
      )}
    </div>
  );
};

type ActionBtnProps = {
  label: string | React.ReactNode;
  onClick: () => void;
  disabled?: boolean;
  shortcut?: string;
  loading?: boolean;
  height?: number;
  icon?: React.ReactNode;
};

/** Kbd chip горе-дясно в бутон (⏎ е Unicode глиф — рендира по-дребно, бъмпваме).
 *  ЕДИНСТВЕНАТА имплементация — ползва се и от модалните бутони, и от
 *  TableFooter, за да не се разминават размер/позиция с пиксел-два. */
export const KbdChip = ({
  shortcut,
  colorClass,
}: {
  shortcut: string;
  colorClass: string;
}) => (
  <span
    className={`pointer-events-none absolute right-2 font-pos-mono font-semibold leading-none ${
      shortcut === "⏎" ? "top-0.5 text-[14px]" : "top-1 text-[10px]"
    } ${colorClass}`}
  >
    {shortcut}
  </span>
);

/** Височина на бутоните във футъра на модал: 54 на широк екран, 48 на тесен (телефон).
 *  `ModalFooter` я налага сам; собствените футъри (клавиатурните модали, прегледите
 *  на отчетите) я подават през `height`. */
// eslint-disable-next-line react-refresh/only-export-components
export const footerButtonHeight = (isNarrow: boolean) => (isNarrow ? 48 : 54);

const btnBase =
  "relative inline-flex w-full cursor-pointer items-center justify-center gap-2 rounded-[14px] whitespace-nowrap transition-colors disabled:cursor-not-allowed disabled:opacity-45";

export const GhostButton = ({
  label,
  onClick,
  disabled,
  shortcut,
  height = 54,
  icon,
}: ActionBtnProps) => (
  <button
    type="button"
    onClick={onClick}
    disabled={disabled}
    // Прозрачен фон — бутонът поема цвета на футъра под себе си (бял или
    // кремав „panel"); hover-ът е с една идея по-тъмния canvas и за двата
    className={`${btnBase} border border-pos-ctl bg-transparent px-4 text-[15px] font-semibold text-pos-ink2 hover:bg-pos-canvas`}
    style={{ height }}
  >
    {icon}
    <span>{label}</span>
    {shortcut && <KbdChip shortcut={shortcut} colorClass="text-pos-faint" />}
  </button>
);

export const PrimaryButton = ({
  label,
  onClick,
  disabled,
  shortcut,
  loading,
  height = 54,
  icon,
}: ActionBtnProps) => (
  <button
    type="button"
    onClick={onClick}
    disabled={disabled || loading}
    className={`${btnBase} border-0 bg-pos-accent px-[18px] text-[15px] font-bold text-white hover:bg-pos-accent-ink`}
    style={{ height }}
  >
    {/* Спинерът наследява цвета на текста (бял върху accent фона) — както
        FooterButton в TableFooter; чака се принтерът, не скрит рендер */}
    {loading ? <LoadingOutlined spin /> : icon}
    {/* Дълъг етикет на тесен телефон се реже с „…", не излиза извън бутона. */}
    <span className="min-w-0 truncate">{label}</span>
    {shortcut && <KbdChip shortcut={shortcut} colorClass="text-white/60" />}
  </button>
);

export const SoftPrimaryButton = ({
  label,
  onClick,
  disabled,
  shortcut,
  loading,
  height = 54,
  icon,
}: ActionBtnProps) => (
  <button
    type="button"
    onClick={onClick}
    disabled={disabled || loading}
    className={`${btnBase} border border-pos-comment-brd bg-pos-ready-bg px-[18px] text-[15px] font-bold text-pos-accent-ink hover:bg-pos-marked`}
    style={{ height }}
  >
    {loading ? <LoadingOutlined spin /> : icon}
    <span>{label}</span>
    {shortcut && (
      <KbdChip shortcut={shortcut} colorClass="text-pos-accent-ink/70" />
    )}
  </button>
);

export const DangerButton = ({
  label,
  onClick,
  disabled,
  loading,
  height = 54,
  icon,
}: ActionBtnProps) => (
  <button
    type="button"
    onClick={onClick}
    disabled={disabled || loading}
    className={`${btnBase} border border-pos-danger-brd bg-pos-danger-bg px-[18px] text-[15px] font-bold text-pos-danger-ink hover:bg-pos-disc-bg`}
    style={{ height }}
  >
    {loading ? <LoadingOutlined spin /> : icon}
    <span>{label}</span>
  </button>
);

export const ModalFooter = ({
  isNarrow,
  children,
  tone = "card",
  columns,
  narrowFirst,
}: {
  isNarrow: boolean;
  children: React.ReactNode;
  /** „panel" = кремав футър (модалът за закриване); default бял. */
  tone?: "card" | "panel";
  /** Пропорции на колоните на широк екран (напр. "1fr 2fr 2fr" — по-тесен
   *  „Отказ"); default — равни. На тесен екран не важи (виж по-долу). */
  columns?: string;
  /** Първият бутон (ghost „Отказ") тесен: 1:3 при два бутона, 1:2:2… при повече.
   *  Само на широк екран; на тесен важи стакингът по-долу. */
  narrowFirst?: boolean;
}) => {
  // Бутоните винаги заемат цялата широчина на footer-а, разпределени равномерно
  // (2 бутона → 50/50), независимо от големината на екрана.
  // На тесен екран всички бутони са 48px, независимо какво подават децата.
  const items = React.Children.toArray(children)
    .filter(Boolean)
    .map((child) =>
      isNarrow && React.isValidElement<{ height?: number }>(child)
        ? React.cloneElement(child, { height: footerButtonHeight(true) })
        : child,
    );
  const toneClass = tone === "panel" ? "bg-pos-panel" : "bg-pos-card";

  // На тесен екран 3+ бутона не се събират на един ред (етикетите се режат) —
  // действията се подреждат по два на ред (нечетен брой → последното на цял
  // ред), а първият бутон (по конвенция ghost „Отказ") минава сам най-отдолу.
  if (isNarrow && items.length >= 3) {
    const [ghost, ...actions] = items;
    return (
      <div
        className={`grid grid-cols-2 gap-2 border-t border-pos-line2 ${toneClass} px-4 py-3`}
      >
        {actions.map((child, i) => (
          <div
            key={i}
            className={`flex ${
              i === actions.length - 1 && actions.length % 2 === 1
                ? "col-span-2"
                : ""
            }`}
          >
            {child}
          </div>
        ))}
        <div className="flex col-span-2">{ghost}</div>
      </div>
    );
  }

  return (
    <div
      className={`grid border-t border-pos-line2 ${toneClass} ${
        isNarrow ? "gap-2 px-4 py-3" : "gap-2.5 px-[22px] py-3.5"
      }`}
      style={{
        gridTemplateColumns:
          columns ??
          (narrowFirst && items.length >= 2
            ? `1fr repeat(${items.length - 1}, ${items.length === 2 ? 3 : 2}fr)`
            : `repeat(${items.length || 1}, 1fr)`),
      }}
    >
      {items.map((child, i) => (
        <div key={i} className="flex">
          {child}
        </div>
      ))}
    </div>
  );
};
